'use client';

import { useCallback, useRef, useState } from 'react';

import { AddressAutocomplete } from '@/components/forms/address-autocomplete';
import { EstimateResultCard } from '@/components/home-value/estimate-result-card';
import { type AddressSuggestion, resolveAddressComponents } from '@/lib/address';
import { HOME_VALUE_API_PATH } from '@/lib/home-value/content';
import type { EstimateResult, HomeValueEstimate } from '@/lib/home-value/types';
import { startSellerFlow } from '@/lib/seller-handoff';
import { opendoorUrl } from '@/lib/urls';

type WidgetState =
  | { phase: 'idle' }
  | { phase: 'loading'; address: string }
  | { phase: 'covered'; estimate: HomeValueEstimate }
  | { phase: 'no_coverage' }
  | { phase: 'error'; message: string };

export function HomeValueEstimator({ placeholder }: { placeholder?: string }) {
  const [state, setState] = useState<WidgetState>({ phase: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const selectedRef = useRef<AddressSuggestion | null>(null);
  const sessionTokenRef = useRef('');

  const reset = useCallback(() => {
    abortRef.current?.abort();
    selectedRef.current = null;
    setSubmitting(false);
    setState({ phase: 'idle' });
  }, []);

  const handleGetOffer = useCallback(() => {
    const suggestion = selectedRef.current;
    if (!suggestion || submitting) return;
    setSubmitting(true);
    void (async () => {
      const components = await resolveAddressComponents(suggestion, sessionTokenRef.current);
      if (components) {
        await startSellerFlow(components, 'HOME_VALUE');
      } else {
        window.location.href = opendoorUrl('/address-entry');
      }
    })();
  }, [submitting]);

  const handleSelect = useCallback((suggestion: AddressSuggestion, sessionToken: string) => {
    selectedRef.current = suggestion;
    sessionTokenRef.current = sessionToken;

    // Valuation keys on the Casa address token; Google suggestions don't have
    // one. Skip the call and show the no-estimate card (still offers the
    // cash-offer CTA) rather than navigating away.
    if (!suggestion.addressToken) {
      setState({ phase: 'no_coverage' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ phase: 'loading', address: suggestion.description });

    void (async () => {
      try {
        const res = await fetch(HOME_VALUE_API_PATH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address: suggestion.description,
            addressToken: suggestion.addressToken,
          }),
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const result = (await res.json()) as EstimateResult;
        if (controller.signal.aborted) return;
        if (result.status === 'covered') {
          setState({ phase: 'covered', estimate: result.estimate });
        } else if (result.status === 'no_coverage') {
          setState({ phase: 'no_coverage' });
        } else {
          setState({ phase: 'error', message: result.message });
        }
      } catch (err) {
        if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
          return;
        }
        setState({
          phase: 'error',
          message: 'Could not estimate this home’s value right now. Please try again.',
        });
      }
    })();
  }, []);

  if (state.phase === 'covered') {
    return (
      <EstimateResultCard
        estimate={state.estimate}
        submitting={submitting}
        onGetOffer={handleGetOffer}
        onReset={reset}
      />
    );
  }

  if (state.phase === 'loading') {
    return (
      <div className="rounded-3xl bg-canvas-deep p-6 sm:p-8" role="status" aria-live="polite">
        <p className="text-ink-muted text-body-md">Estimating the value of {state.address}…</p>
        <div className="mt-6 h-12 w-2/3 animate-pulse rounded-xl bg-state-hover" />
        <div className="mt-3 h-5 w-1/2 animate-pulse rounded-lg bg-state-hover" />
      </div>
    );
  }

  if (state.phase === 'no_coverage' || state.phase === 'error') {
    const message =
      state.phase === 'no_coverage'
        ? 'We don’t have enough data to estimate this home automatically. You can still request a no-obligation cash offer and Opendoor will evaluate your home directly.'
        : state.message;
    return (
      <div className="rounded-3xl bg-canvas-deep p-6 sm:p-8" role="alert">
        <p className="text-ink text-body-md">{message}</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleGetOffer}
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 font-medium text-ink-light transition hover:bg-canvas-dark disabled:opacity-60 text-body-sm"
          >
            {submitting ? 'Starting…' : 'Get my cash offer'}
            {!submitting && <span aria-hidden>→</span>}
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex flex-1 items-center justify-center rounded-full border border-line-strong px-6 py-3 font-medium text-ink transition hover:bg-state-hover text-body-sm"
          >
            Try another address
          </button>
        </div>
      </div>
    );
  }

  return <AddressAutocomplete placeholder={placeholder} onSelect={handleSelect} />;
}
